/**
 * Reading and writing the admin import files. Both libraries load on first use, so the grower
 * app never downloads them.
 */

export type Cell = string | number | null;

export interface ColumnSpec {
  /** Heading as written in the template's first row. */
  header: string;
  /** Key the value is stored under. */
  key: string;
  required: boolean;
}

export type SheetResult =
  | { ok: true; rows: Array<{ row: number; cells: Record<string, Cell> }> }
  | { ok: false; reason: 'notExcel' | 'columns' | 'empty' };

const normal = (s: unknown) =>
  String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

function toCell(v: unknown): Cell {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number' || typeof v === 'string') return v;
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v);
}

/**
 * Map a sheet's rows to the columns by their headings in the first row, in any order.
 * Row numbers are the Excel row numbers (the heading is row 1). Blank rows are dropped.
 */
export function mapSheet(
  data: ReadonlyArray<ReadonlyArray<unknown>>,
  columns: readonly ColumnSpec[],
): SheetResult {
  const head = (data[0] ?? []).map(normal);
  const index = new Map<string, number>();
  for (const c of columns) {
    const i = head.indexOf(normal(c.header));
    if (i >= 0) index.set(c.key, i);
    else if (c.required) return { ok: false, reason: 'columns' };
  }
  const rows: Array<{ row: number; cells: Record<string, Cell> }> = [];
  data.slice(1).forEach((r, n) => {
    const cells: Record<string, Cell> = {};
    let any = false;
    for (const c of columns) {
      const i = index.get(c.key);
      const v = i === undefined ? null : toCell(r[i]);
      if (v !== null && String(v).trim() !== '') any = true;
      cells[c.key] = v;
    }
    if (any) rows.push({ row: n + 2, cells });
  });
  if (rows.length === 0) return { ok: false, reason: 'empty' };
  return { ok: true, rows };
}

/** Read the first sheet of an .xlsx file. Anything that is not a readable .xlsx is `notExcel`. */
export async function readSheetFile(
  file: File,
  columns: readonly ColumnSpec[],
): Promise<SheetResult> {
  if (!/\.xlsx$/i.test(file.name)) return { ok: false, reason: 'notExcel' };
  try {
    const { readSheet } = await import('read-excel-file/browser');
    const data = await readSheet(file);
    return mapSheet(data as ReadonlyArray<ReadonlyArray<unknown>>, columns);
  } catch {
    return { ok: false, reason: 'notExcel' };
  }
}

export type SheetValue = string | number;

/**
 * Save a sheet with the column headings in bold on the first row and a row per record. With no
 * rows it is the empty template, so the same download serves both: take the current data out,
 * change it, put it back.
 */
export async function saveSheet(
  fileName: string,
  headers: readonly string[],
  rows: ReadonlyArray<ReadonlyArray<SheetValue>> = [],
): Promise<void> {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');
  const head = headers.map((h) => ({ value: h, fontWeight: 'bold' as const }));
  const body = rows.map((row) =>
    row.map((value) =>
      typeof value === 'number' ? { value, type: Number } : { value, type: String },
    ),
  );
  await writeXlsxFile([head, ...body], {
    columns: headers.map(() => ({ width: 18 })),
  }).toFile(fileName);
}
